import Icon from '@/components/ui/AppIcon';

interface VisaApplication {
  id: string;
  applicationType: string;
  destination: string;
  nationality: string;
  applicationNumber: string;
  submittedDate: string;
  currentStatus: 'submitted' | 'under-review' | 'additional-docs-required' | 'approved' | 'rejected' | 'ready-for-collection';
  embassy: string;
  estimatedCompletionDate: string;
  urgencyLevel: 'low' | 'medium' | 'high' | 'critical';
  travelDate: string;
}

interface VisaApplicationCardProps {
  application: VisaApplication;
  isSelected: boolean;
  onClick: () => void;
}

export default function VisaApplicationCard({ application, isSelected, onClick }: VisaApplicationCardProps) {
  const statusConfig = {
    'submitted': { label: 'Submitted', color: 'bg-sky-blue/10 text-sky-blue border-sky-blue/30', icon: 'DocumentCheckIcon' },
    'under-review': { label: 'Under Review', color: 'bg-warning/10 text-warning border-warning/30', icon: 'ClockIcon' },
    'additional-docs-required': { label: 'Docs Required', color: 'bg-coral/10 text-coral border-coral/30', icon: 'ExclamationCircleIcon' },
    'approved': { label: 'Approved', color: 'bg-trust-green/10 text-trust-green border-trust-green/30', icon: 'CheckCircleIcon' },
    'rejected': { label: 'Rejected', color: 'bg-error/10 text-error border-error/30', icon: 'XCircleIcon' },
    'ready-for-collection': { label: 'Ready', color: 'bg-primary/10 text-primary border-primary/30', icon: 'ArchiveBoxIcon' }
  };

  const urgencyConfig = {
    'low': { color: 'bg-muted', label: 'Low' },
    'medium': { color: 'bg-warning', label: 'Medium' },
    'high': { color: 'bg-coral', label: 'High' },
    'critical': { color: 'bg-error', label: 'Critical' }
  };

  const status = statusConfig[application.currentStatus];
  const urgency = urgencyConfig[application.urgencyLevel];

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-xl p-4 shadow-brand border-2 transition-all duration-200 hover:shadow-lg ${
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/30'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-heading font-bold text-foreground mb-1">
            {application.destination}
          </h3>
          <p className="text-sm text-muted-foreground">{application.applicationType}</p>
        </div>
        <div className={`w-3 h-3 rounded-full ${urgency.color} animate-pulse`} />
      </div>

      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border-2 mb-3 ${status.color}`}>
        <Icon name={status.icon as any} size={14} />
        {status.label}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="HashtagIcon" size={14} />
          <span className="font-mono">{application.applicationNumber}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="BuildingOfficeIcon" size={14} />
          <span>{application.embassy}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="CalendarIcon" size={14} />
          <span>Travel: {new Date(application.travelDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
        </div>
      </div>
    </button>
  );
}